import axios from 'axios';

const API_URL = import.meta.env.VITE_API_URL || 'https://dev.api.gioallday.com';

export type SuperAdminUserSummary = {
  id: string;
  email: string | null;
  phone: string | null;
  firstName: string | null;
  lastName: string | null;
  businessName: string | null;
  userType: string;
  role: string;
  status: string;
  isViewOnly: boolean;
  createdAt: string;
  updatedAt: string;
};

export type SuperAdminRestaurantSummary = SuperAdminUserSummary & {
  source: 'signup' | 'invite' | 'other';
  inviteStatus: 'accepted' | 'pending';
};

export type SuperAdminSupplierDirectoryItem = SuperAdminUserSummary & {
  employeeCount: number;
  restaurantCount: number;
  orderCount: number;
};

export type SuperAdminUsersResponse = {
  items: SuperAdminUserSummary[];
  total: number;
  page: number;
  limit: number;
  totalPages: number;
};

export type SuperAdminRestaurantsResponse = {
  items: SuperAdminRestaurantSummary[];
  total: number;
  page: number;
  limit: number;
  totalPages: number;
};

export type SuperAdminSuppliersDirectoryResponse = {
  items: SuperAdminSupplierDirectoryItem[];
  total: number;
  page: number;
  limit: number;
  totalPages: number;
};

export type SuperAdminAnalyticsOverview = {
  usersByType: Record<string, Record<string, number>>;
  pendingAccessRequests: number;
  totals: {
    restaurants: number;
    suppliers: number;
    employees: number;
    platform: number;
  };
  ordersLast7Days: { total: number; byStatus: Record<string, number> };
  ordersLast30Days: { total: number; byStatus: Record<string, number> };
  topSuppliers: {
    supplierId: string;
    businessName: string | null;
    orderCount: number;
  }[];
  topRestaurants: {
    restaurantId: string;
    businessName: string | null;
    orderCount: number;
  }[];
};

export type SuperAdminSupplierOption = {
  id: string;
  businessName: string | null;
  email: string | null;
  phone: string | null;
  status: string;
};

export type SuperAdminSupplierOrderSessionResponse = {
  accessToken: string;
  expiresIn: string;
  supplier: {
    id: string;
    businessName: string | null;
    email: string | null;
    type: string;
    role: string;
    first_name: string | null;
    last_name: string | null;
    phone: string | null;
    status: string;
  };
};

export type SuperAdminSupplierDetail = {
  supplier: SuperAdminUserSummary;
  employees: (SuperAdminUserSummary & { isSelf: boolean })[];
  restaurants: (SuperAdminRestaurantSummary & { linkedAt: string })[];
  orders: {
    items: {
      id: string;
      orderNumber: string;
      orderName: string | null;
      status: string;
      restaurantId: string;
      restaurantName: string | null;
      createdAt: string;
      updatedAt: string;
    }[];
    total: number;
    page: number;
    limit: number;
    totalPages: number;
  };
  counts: {
    employees: number;
    restaurants: number;
    orders: number;
  };
};

export type SuperAdminRestaurantDetail = {
  restaurant: SuperAdminRestaurantSummary;
  employees: (SuperAdminUserSummary & {
    locationId: string | null;
    locationName: string | null;
  })[];
  suppliers: (SuperAdminUserSummary & {
    linkedAt: string;
    networkType: 'in_network' | 'out_of_network';
  })[];
  locations: {
    id: string;
    name: string | null;
    formattedAddress: string | null;
    city: string | null;
    state: string | null;
    isDefault: boolean;
    isActive: boolean;
    supplierCount: number;
    createdAt: string;
  }[];
  orders: {
    items: {
      id: string;
      orderNumber: string;
      orderName: string | null;
      status: string;
      supplierId: string | null;
      supplierName: string | null;
      createdAt: string;
      updatedAt: string;
    }[];
    total: number;
    page: number;
    limit: number;
    totalPages: number;
  };
  counts: {
    employees: number;
    suppliers: number;
    locations: number;
    orders: number;
  };
};

const authHeaders = (token: string) => ({
  Authorization: `Bearer ${token}`,
  'Content-Type': 'application/json'
});

export const superAdminApi = {
  listUsers: (
    token: string,
    params: {
      page?: number;
      limit?: number;
      search?: string;
      user_type?: string;
      status?: string;
      role?: string;
    }
  ) =>
    axios.get<SuperAdminUsersResponse>(`${API_URL}/api/super-admin/users`, {
      headers: authHeaders(token),
      params
    }),

  updateUserStatus: (
    token: string,
    userId: string,
    status: 'active' | 'inactive'
  ) =>
    axios.patch(
      `${API_URL}/api/super-admin/users/${userId}/status`,
      { status },
      { headers: authHeaders(token) }
    ),

  getAnalytics: (token: string) =>
    axios.get<SuperAdminAnalyticsOverview>(
      `${API_URL}/api/super-admin/analytics/overview`,
      { headers: authHeaders(token) }
    ),

  listRestaurants: (
    token: string,
    params: {
      page?: number;
      limit?: number;
      search?: string;
      status?: string;
      source?: string;
    }
  ) =>
    axios.get<SuperAdminRestaurantsResponse>(
      `${API_URL}/api/super-admin/restaurants`,
      {
        headers: authHeaders(token),
        params
      }
    ),

  listSuppliersDirectory: (
    token: string,
    params: {
      page?: number;
      limit?: number;
      search?: string;
      status?: string;
    }
  ) =>
    axios.get<SuperAdminSuppliersDirectoryResponse>(
      `${API_URL}/api/super-admin/suppliers/directory`,
      {
        headers: authHeaders(token),
        params
      }
    ),

  getSupplierDetail: (
    token: string,
    supplierId: string,
    params?: { ordersPage?: number; ordersLimit?: number }
  ) =>
    axios.get<SuperAdminSupplierDetail>(
      `${API_URL}/api/super-admin/suppliers/${supplierId}`,
      {
        headers: authHeaders(token),
        params
      }
    ),

  getRestaurantDetail: (
    token: string,
    restaurantId: string,
    params?: { ordersPage?: number; ordersLimit?: number }
  ) =>
    axios.get<SuperAdminRestaurantDetail>(
      `${API_URL}/api/super-admin/restaurants/${restaurantId}`,
      {
        headers: authHeaders(token),
        params
      }
    ),

  listSuppliers: (token: string, search?: string) =>
    axios.get<SuperAdminSupplierOption[]>(`${API_URL}/api/super-admin/suppliers`, {
      headers: authHeaders(token),
      params: search ? { search } : undefined
    }),

  getOrderUnlockSettings: (token: string) =>
    axios.get<{ isSet: boolean; updatedAt: string | null }>(
      `${API_URL}/api/super-admin/settings/order-unlock-code`,
      { headers: authHeaders(token) }
    ),

  setOrderUnlockCode: (
    token: string,
    body: { unlockCode: string; currentUnlockCode?: string }
  ) =>
    axios.put<{ isSet: boolean; updatedAt: string | null }>(
      `${API_URL}/api/super-admin/settings/order-unlock-code`,
      body,
      { headers: authHeaders(token) }
    ),

  /** Open supplier order workspace (short-lived scoped token; used only in-panel). */
  openSupplierOrderSession: (
    token: string,
    supplierId: string,
    unlockCode: string
  ) =>
    axios.post<SuperAdminSupplierOrderSessionResponse>(
      `${API_URL}/api/super-admin/supplier-order-session/${supplierId}`,
      { unlockCode },
      { headers: authHeaders(token) }
    )
};
